import { createContext, ReactNode, useCallback, useContext, useLayoutEffect, useMemo, useState } from 'react';
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import { get } from 'lodash';
import { useDebounceValue } from 'usehooks-ts';

import { normalizePath } from 'services/webedi/webedi';

// Type definitions
type FieldArrayActions = Pick<ReturnType<typeof useFieldArray>, 'fields' | 'append' | 'remove'>;

type CustomSetValue = {
  setValue: (
    name: string | undefined,
    value: any,
    options?: Parameters<ReturnType<typeof useFormContext>['setValue']>[2],
  ) => void;
};

type RemoveEmbedded = {
  removeEmbedded: (rowIndex: number, embeddedRowIndex: number, columnGroupName: string) => void;
};

type AddEmbedded = {
  addEmbedded: (rowIndex: number, columnGroupName: string) => void;
};

type FormUtilities = Pick<ReturnType<typeof useFormContext>, 'register' | 'resetField'>;

type DataTableContextValue = FieldArrayActions & CustomSetValue & RemoveEmbedded & AddEmbedded & FormUtilities;

// Context creation
const DataTableContext = createContext<DataTableContextValue | null>(null);

const EMPTY_ARRAY: Array<any> = [];
const DEBOUNCE_DELAY = process.env.NODE_ENV === 'test' ? 50 : 500;

// Custom hook for field synchronization
// We want to have a debounced version of the fields to avoid unnecessary re-renders
// But when we append we want that to be reflected in the fields immediately
// So we have a syncedFields state that is updated from debouncedFields
// We use setSyncedFields to be called when we append something so the user can see it happening right away

function useSynchronizedFields(name: string) {
  const fields = useWatch({ name });
  const [debouncedFields, setDebouncedFields] = useDebounceValue(fields, DEBOUNCE_DELAY);
  const [syncedFields, setSyncedFields] = useState(fields);

  // Effect to update debouncedFields
  useLayoutEffect(() => {
    setDebouncedFields(fields || EMPTY_ARRAY);
  }, [fields, setDebouncedFields]);

  // Effect to update syncedFields from debouncedFields
  useLayoutEffect(() => {
    setSyncedFields(debouncedFields || EMPTY_ARRAY);
  }, [debouncedFields]);

  return [syncedFields || [], setSyncedFields];
}

/**
 * Higher-Order Component that wraps a component with DataTableProvider
 * @param Component The component to wrap
 * @param dataPrefix The prefix to use for the data table
 * @returns A wrapped component that has access to DataTableContext
 */
export function withDataTableProvider<T extends object>(Component: React.FC<T>, dataPrefix: string) {
  // eslint-disable-next-line react/display-name
  return (props: T) => {
    return (
      <DataTableProvider name={dataPrefix}>
        <Component {...props} />
      </DataTableProvider>
    );
  };
}

// DataTableProvider component
export function DataTableProvider({ name, children }: { name: string; children: ReactNode }) {
  const {
    setValue,
    register,
    resetField,
    trigger,
    getValues,
    control,
    formState: { isSubmitted },
  } = useFormContext();

  const mode = control._options?.mode;
  const revalidateMode = control._options?.reValidateMode;
  const shouldTriggerValidation = mode !== 'onSubmit' && revalidateMode !== 'onSubmit';

  const [syncedFields, setSyncedFields] = useSynchronizedFields(name);
  // Custom setValue function to handle nested fields
  const handleSetValue: CustomSetValue['setValue'] = useCallback(
    (fieldName, value, options) => {
      if (!fieldName) {
        setValue(name, value, options);
      } else {
        const nestedField = fieldName.replace(`${name}.`, '');
        setValue(`${name}.${nestedField}`, value, options);
      }
      if (isSubmitted && shouldTriggerValidation && options?.shouldValidate !== false) {
        trigger();
      }
    },
    [name, setValue, trigger, isSubmitted, shouldTriggerValidation],
  );

  /**
   * Custom append function to handle the sync between field array and the values
   * Should not be called multiple times in a row, as it will not update the values correctly (syncedFields will be outdated)
   * TODO: Fix above issue
   */
  const handleAppend: ReturnType<typeof useFieldArray>['append'] = useCallback(
    (value) => {
      const currentValues = getValues(name) || [];
      const formattedValue = [value].flat();
      const newValues = [...currentValues, ...formattedValue];

      setSyncedFields(newValues);
      setValue(name, newValues, { shouldDirty: true });

      if (isSubmitted && shouldTriggerValidation) {
        trigger();
      }
    },
    [getValues, name, isSubmitted, shouldTriggerValidation, setSyncedFields, setValue, trigger],
  );

  /**
   * Custom remove function to handle the sync between field array and the values
   * Should not be called multiple times in a row, as it will not update the values correctly (syncedFields will be outdated)
   * TODO: Fix above issue
   */
  const handleRemove: ReturnType<typeof useFieldArray>['remove'] = useCallback(
    (args) => {
      const currentValues = getValues(name) as Array<any>;
      if (typeof args === 'number') {
        const newValue = currentValues.toSpliced(args, 1);
        setValue(name, newValue, { shouldDirty: true });
        setSyncedFields(newValue);
      } else {
        throw new Error('DataTableContext multiple removal is not yet supported');
      }

      if (isSubmitted && shouldTriggerValidation) {
        trigger();
      }
    },
    [getValues, name, isSubmitted, shouldTriggerValidation, setValue, setSyncedFields, trigger],
  );

  const handleRemoveEmbedded: RemoveEmbedded['removeEmbedded'] = useCallback(
    (rowIndex: number, embeddedRowIndex: number, columnGroupName: string) => {
      const currentValues = getValues(name) as Array<any>;
      const currentRowValue = currentValues[rowIndex];
      const pathToItems = normalizePath(columnGroupName);
      const currentEmbeddedItems = get(currentRowValue, pathToItems);

      if (!Array.isArray(currentEmbeddedItems)) {
        throw new Error(`Field at path ${columnGroupName} is not an array`);
      }

      const updatedItems = [
        ...currentEmbeddedItems.slice(0, embeddedRowIndex),
        ...currentEmbeddedItems.slice(embeddedRowIndex + 1),
      ];
      const updatedRow = {
        ...currentRowValue,
        [columnGroupName]: updatedItems,
      };

      const updatedValues = [...currentValues];
      updatedValues[rowIndex] = updatedRow;

      // When removing an array item, indices shift (e.g., item at index 2 becomes index 1).
      // React Hook Form maintains internal field registrations by path (e.g., 'data.0.discounts.2').
      // Without resetField, RHF keeps registrations for old paths, causing misaligned validation state
      // and stale field metadata. resetField clears all registrations under this path and forces
      // fields to re-register with correct indices on next render.
      resetField(`${name}.${rowIndex}.${columnGroupName}`, { defaultValue: updatedItems });
      setValue(`${name}.${rowIndex}`, updatedRow, { shouldDirty: true });
      setSyncedFields(updatedValues);

      if (isSubmitted && shouldTriggerValidation) {
        trigger();
      }
    },
    [getValues, name, resetField, setSyncedFields, setValue, isSubmitted, shouldTriggerValidation, trigger],
  );

  const handleAddEmbedded: AddEmbedded['addEmbedded'] = useCallback(
    (rowIndex: number, columnGroupName: string) => {
      const currentValues = getValues(name) as Array<any>;
      const currentRowValue = currentValues[rowIndex];
      const pathToItems = normalizePath(columnGroupName);
      const currentEmbeddedItems = get(currentRowValue, pathToItems);

      if (!Array.isArray(currentEmbeddedItems) && currentEmbeddedItems) {
        throw new Error(`Field at path ${columnGroupName} is not an array`);
      }

      const updatedItems = [...(currentEmbeddedItems || []), {}];
      const updatedRow = {
        ...currentRowValue,
        [columnGroupName]: updatedItems,
      };

      const updatedValues = [...currentValues];
      updatedValues[rowIndex] = updatedRow;

      // When adding an array item, React Hook Form needs to register new fields at the new indices.
      // resetField clears existing registrations and ensures all fields re-register cleanly with
      // the expanded array structure, preventing stale field state from persisting.
      resetField(`${name}.${rowIndex}.${columnGroupName}`, { defaultValue: updatedItems });
      setValue(`${name}.${rowIndex}`, updatedRow, { shouldDirty: true });
      setSyncedFields(updatedValues);

      if (isSubmitted && shouldTriggerValidation) {
        trigger();
      }
    },
    [getValues, name, resetField, setValue, setSyncedFields, isSubmitted, shouldTriggerValidation, trigger],
  );

  const handleRegister: typeof register = useCallback(
    (...args) => {
      const registerResult = register(...args);
      return {
        ...registerResult,
        onChange: (e) => {
          return new Promise((resolve, reject) => {
            registerResult
              .onChange(e)
              .then(() => {
                if (isSubmitted && shouldTriggerValidation) {
                  trigger();
                }
                resolve();
              })
              .catch(reject);
          });
        },
      };
    },
    [register, isSubmitted, shouldTriggerValidation, trigger],
  );

  // Memoized context value
  const contextValue = useMemo<DataTableContextValue>(
    () => ({
      append: handleAppend,
      remove: handleRemove,
      removeEmbedded: handleRemoveEmbedded,
      addEmbedded: handleAddEmbedded,
      fields: syncedFields,
      setValue: handleSetValue,
      register: handleRegister,
      resetField,
    }),
    [
      handleAppend,
      handleRemove,
      handleRemoveEmbedded,
      handleAddEmbedded,
      syncedFields,
      handleSetValue,
      handleRegister,
      resetField,
    ],
  );

  return <DataTableContext.Provider value={contextValue}>{children}</DataTableContext.Provider>;
}

// Custom hook to use the DataTableContext
export function useDataTableContext() {
  const context = useContext(DataTableContext);

  if (!context) {
    throw new Error('useDataTableContext must be used within a DataTableProvider');
  }
  return context;
}
